import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faCheckCircle,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

const ProfileRichnessIndicator = ({
  score = 0,
  size = "md",
  showLabel = true,
  showTooltip = true,
  className = "",
  onClick,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  // Define size classes
  const sizeClasses = {
    sm: "h-1.5 w-16 text-xs",
    md: "h-2 w-24 text-sm",
    lg: "h-3 w-32 text-base",
  };

  // Get color based on score
  const getColor = (score) => {
    if (score < 30) return "bg-red-500";
    if (score < 60) return "bg-yellow-500";
    if (score < 80) return "bg-green-500";
    return "bg-emerald-500";
  };

  // Get label based on score
  const getLabel = (score) => {
    if (score < 30) return t("profile.richness.poor");
    if (score < 60) return t("profile.richness.basic");
    if (score < 80) return t("profile.richness.good");
    return t("profile.richness.complete");
  };

  // Get icon based on score
  const getIcon = (score) => {
    if (score < 60) return faInfoCircle;
    if (score < 80) return faChartLine;
    return faCheckCircle;
  };

  // Component for tooltip content
  const TooltipContent = () => (
    <div className="p-3 max-w-xs">
      <h4 className="font-medium mb-2">{t("profile.richness.title")}</h4>
      <p className="text-xs mb-3">{t("profile.richness.description")}</p>

      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span>{t("profile.richness.basic_info")}</span>
          <span className="font-medium">20%</span>
        </div>
        <div className="flex justify-between text-xs">
          <span>{t("profile.richness.additional_details")}</span>
          <span className="font-medium">20%</span>
        </div>
        <div className="flex justify-between text-xs">
          <span>{t("profile.richness.profile_description")}</span>
          <span className="font-medium">20%</span>
        </div>
        <div className="flex justify-between text-xs">
          <span>{t("profile.richness.images")}</span>
          <span className="font-medium">30%</span>
        </div>
        <div className="flex justify-between text-xs">
          <span>{t("profile.richness.tags")}</span>
          <span className="font-medium">10%</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      <div
        className={`flex items-center gap-1.5 group ${
          onClick ? "cursor-pointer" : ""
        }`}
        onClick={() => {
          if (onClick) onClick();
          if (showTooltip) setShowDetails(!showDetails);
        }}
      >
        {/* Progress bar */}
        <div
          className={`bg-gray-200 rounded-full overflow-hidden ${sizeClasses[size]} dark:bg-gray-700`}
        >
          <div
            className={`${getColor(
              score
            )} h-full rounded-full transition-all duration-500 ease-out`}
            style={{ width: `${score}%` }}
          ></div>
        </div>

        {/* Label */}
        {showLabel && (
          <div
            className={`flex items-center gap-1 ${sizeClasses[size]
              .split(" ")
              .pop()} dark:text-gray-400`}
          >
            <FontAwesomeIcon
              icon={getIcon(score)}
              className={
                score < 30
                  ? "text-red-500"
                  : score < 60
                  ? "text-yellow-500"
                  : score < 80
                  ? "text-green-500"
                  : "text-emerald-500"
              }
            />
            <span className="text-gray-600 font-medium dark:text-gray-400">
              {getLabel(score)}
            </span>
          </div>
        )}

        {/* Tooltip */}
        {showTooltip && showDetails && (
          <div className="absolute top-full left-0 mt-2 z-10 bg-white rounded-md shadow-lg border border-gray-200 animate-fade-in dark:bg-gray-800 dark:border-gray-700">
            <TooltipContent />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileRichnessIndicator;
